<template lang="pug">
div
    h3 Step 1: Login to authorise email sending permissions
    b-button.mr-1(size="lg" @click="login" variant="primary")  Authorise Email Sending Permissions
    hr
    
    b-overlay(:show="!token")
        template( #overlay)
            .d-flex.flex-column.align-items-center
                b-icon(icon="three-dots" animation="cylon" font-scale="4")
                h4 Complete Step 1 first
        h3 Step 2: Upload Updated Excel Workbook.
        b-form-file(@input="processXLSX")
        p Use the same format as the export in the previous page
        b-alert(:show="true" dismissible)
            p.mb-0 Each Sheet must contain the following column headers:
            ul.small 
                li(v-for="field in fields") {{ field }}
        p To replace an existing import, upload a new Excel Workbook
        p Note: the "Unassigned Students" sheet tab and empty sheet tabs will be skipped.
        p Note: For Digital Exam deadline, use the DATE field. The reporting Time field will be ignored.
        hr
        b-overlay(:show="sendingEmail")
            template( #overlay)
                .d-flex.flex-column.align-items-center
                    b-icon(icon="three-dots" animation="cylon" font-scale="4")
                    h4 Sending Emails

            h3 Step 3: Check and Send Notifications
            b-checkbox.mb-2(switch v-model="digitalMode") Digital Exam
            .mb-2
                b-button.mr-1(variant="primary" @click="showEmailPreview=true") Preview Email / Edit Common Fields
                b-button(@click="confirmSend(sheets)" :disabled="sendingEmail") Send Email to all Students
            b-tabs
                b-tab(v-for="(sheet,i) in sheets" :title="sheetNames[i]")
                    b-table(:items="sheet" :fields="tableFields")
                        template(#cell(Actions)="{item}")
                            .d-flex
                                b-button.mr-1(@click="previewRow(item)" variant="outline-primary") Preview
                                b-button(@click="confirmSend([item])" :disabled="sendingEmail") Send Email
            b-modal(size="xl" v-model="showRowPreview" hide-footer title="Email Preview" @hidden="clearRowPreview")
                template(v-if="rowPreview")
                    b-alert(:show="rowPreview.problems.length > 0" variant="danger") This email cannot be sent: {{ rowPreview.problems.join(" and ") }}.
                    p.mb-0 <strong>Subject:</strong> {{ rowPreview.title }}
                    p.mb-0 <strong>To:</strong> {{ formatRecipients(rowPreview.toRecipients) }}
                    p.mb-0 <strong>CC:</strong> {{ formatRecipients(rowPreview.ccRecipients) }}
                    p <strong>BCC:</strong> {{ formatRecipients(rowPreview.bccRecipients) }}
                    b-tabs
                        b-tab.pt-2(title="Email")
                            iframe.w-100.border(:srcdoc="rowPreview.body" sandbox="allow-popups allow-popups-to-escape-sandbox" style="height:70vh")
                        b-tab.pt-2(:title="'PDF Attachment: ' + rowPreview.fileName")
                            iframe.w-100.border(v-if="rowPreview.pdfUrl" :src="rowPreview.pdfUrl" style="height:70vh")
                            .text-center.p-5(v-else)
                                b-spinner
                                div Generating PDF...
            b-modal(:visible="!!resumeSending" title="Sign in again to keep sending" no-close-on-backdrop no-close-on-esc hide-header-close)
                p Your Microsoft sign-in has run out, so sending has paused. No emails have been skipped.
                p.mb-0 Sign in again and sending will carry on from the same student.
                template(#modal-footer)
                    b-button(@click="stopSending") Stop sending
                    b-button(variant="primary" @click="signInAndResume") Sign in and continue
            b-modal(size="xl" v-model="showEmailPreview" :hide-footer="!pendingSend" title="Email Preview /Configuration" @hidden="pendingSend=null")
                template(#modal-footer)
                    template(v-if="pendingSend")
                        span.mr-auto Check the links and dates above. Sending {{ pendingSendable }} {{ digitalMode ? "Digital" : "Physical" }} Exam email(s).
                            strong.text-danger(v-if="pendingUnsendable")  {{ pendingUnsendable }} row(s) are missing {{ digitalMode ? "an Email or a readable Date" : "an Email, a readable Date or a Reporting Time" }} and will not be sent.
                        b-button(@click="showEmailPreview=false") Cancel
                        b-button(variant="primary" @click="sendPending" :disabled="!pendingSendable") Send {{ pendingSendable }} email(s)
                b-alert(:show="dateWarnings.length > 0" variant="warning")
                    strong Check the dates and times before sending:
                    ul.mb-0
                        li(v-for="warning in dateWarnings") {{ warning }}
                b-tabs
                    b-tab.p-3(title="Common Fields")
                        b-form-group(label="Email Subject (English)")
                            b-input(v-model="otherDetails.titleEn" @input="saveOtherDetails('titleEn',$event)")
                        b-form-group(label="Email Subject (Chinese)")
                            b-input(v-model="otherDetails.titleZh" @input="saveOtherDetails('titleZh',$event)")
                        b-form-group(label="Venue" description="Ignored for Digital Exam")
                            b-textarea(v-model="otherDetails.venue" rows="6" @input="saveOtherDetails('venue',$event)")
                        b-form-group(label="Deadline for details amendments")
                            b-input(v-model="otherDetails.amendmentsDeadline" type="date" @input="saveOtherDetails('amendmentsDeadline',$event)")
                        b-form-group(label="Link to notes / Guidelines for Digital Exam" description="Must include https:// in the link")
                            b-input(v-model="otherDetails.examNotesLink" type="url" placeholder="https://example.com" @input="saveOtherDetails('examNotesLink',$event)")
                        b-form-group(label="Contact Details" description="e.g. Phone Number, Email etc")
                            b-input(v-model="otherDetails.contactDetails" @input="saveOtherDetails('contactDetails',$event)")
                        hr
                        h5 Digital Exam Only Fields
                        b-form-group(label="Title for additional instructions (EN)")
                            b-input(v-model="otherDetails.digitalAddInstPreEn" @input="saveOtherDetails('digitalAddInstPreEn',$event)")
                        b-form-group(label="Title for additional instructions (ZH)")
                            b-input(v-model="otherDetails.digitalAddInstPreZh" @input="saveOtherDetails('digitalAddInstPreZh',$event)")
                        b-form-group(label="Additional Instructions for Digital Exam (EN)" description="Line breaks will be broken down into numbered points")
                            b-textarea(v-model="otherDetails.digitalAddInstEn" rows="6" @input="saveOtherDetails('digitalAddInstEn',$event)")
                        b-form-group(label="Additional Instructions for Digital Exam (ZH)" description="Line breaks will be broken down into numbered points")
                            b-textarea(v-model="otherDetails.digitalAddInstZh" rows="6" @input="saveOtherDetails('digitalAddInstZh',$event)" )
                        b-form-group(label="Link to submission for Digital Exam" description="Must include https:// in the link")
                            b-input(v-model="otherDetails.digitalSubmitLink" type="url" placeholder="https://example.com" @input="saveOtherDetails('digitalSubmitLink',$event)")
                    b-tab.p-3(title="Preview (Physical Exam)")
                        p.font-weight-bold.mb-0 Subject:
                        div {{ otherDetails.titleEn }} - {{otherDetails.titleZh}}
                        br
                        .border.py-3.px-4
                            .d-flex.justify-content-between
                                b-img(src="/tenglogo.png" style="height:120px")
                                b-img(src="/confinst.png" style="height:120px")
                            hr
                            p.mb-0 
                                strong Candidate Number 考生编号:
                                span.ml-4 XXXXXX
                            p 
                                strong Candidate Name 考生名字:
                                span.ml-4 XXXXXX
                            br
                            h5(class="mb-0") <u>{{ otherDetails.titleEn }}</u>
                            h5 <u>{{ otherDetails.titleZh }}</u>
                            br
                                                
                            p.mb-0 Thank you for your registration. Your exam date and time are scheduled as follow:
                            p 感谢您报名参加华乐器乐考级。您的考级日期及时间如下：
                            .px-5
                                b-table-simple(bordered)
                                    b-tbody
                                        b-tr
                                            b-th Date<br/>日期
                                            b-th {{ startEn }}<br/>{{ startZh }}
                                        b-tr 
                                            b-th Reporting Time <br/>报道时间
                                            b-th 1:40 PM
                                        b-tr
                                            b-th Venue <br/> 地点
                                            b-th
                                                span(style="white-space:pre") {{ otherDetails.venue }}
                            p.mb-0 
                                a(:href="otherDetails.examNotesLink") Please click here to view and download the important notes on the examination details. 
                            p 
                                a(:href="otherDetails.examNotesLink") 请点击这里查阅及下载考级注意事项文件。

                            p.mb-0 Kindly also verify your particulars below. If there are any amendments to be made, please email us before <u>{{dayjs(otherDetails.amendmentsDeadline).format("DD MMMM YYYY (ddd)")}}</u>. No action is required if all particulars are correct.
                            p 请您也检查并确认以下个人资料。若有任何资料需要修改，请在<u>{{formatChineseDate(otherDetails.amendmentsDeadline)}}</u>之前电邮通知我们。若所有资料无误，您无需采取任何行动。

                            p.mb-0.font-weight-bold The certificate cannot be reprinted once verification is done.
                            p.font-weight-bold 一旦确认完成，证书将不得重印。
                            
                            p.mb-0 If you have any queries, kindly contact us at:
                            p 若有任何疑问，可拨电或电邮：

                            p.font-weight-bold {{otherDetails.contactDetails}}

                            p.mb-0 Thank you for your support. All the best for your examination!
                            p 感谢您的支持。祝您考级顺利！

                            hr
                            b-table-simple(bordered)
                                b-tbody
                                    b-tr 
                                        b-td Candidate Number<br/>考生编号
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Name of Candidate <br/>考生姓名
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Name in Chinese  <br/>中文名
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Instrument   <br/>乐器
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Grade    <br/>级别
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Exam Score Serial No.     <br/>乐谱编号
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Instructor    <br/>指导老师
                                        b-td XXXXXX
                                    b-tr
                                        b-td Results Mailing Option    <br/>考级成绩邮寄方式
                                        b-td XXXXXX
                                    b-tr
                                        b-td Mailing address <br/>地址
                                        b-td XXXX XXXXXX, Singapore XXXXX
                            p.mb-0
                                i The following information is for purposes related to the Examinations and identification of candidate.
                            p
                                i 以下资料只供于考级相关用途及确认考生身份。
                            b-table-simple(bordered)
                                b-tbody
                                    b-tr
                                        b-td NRIC/Passport No (Last 4 Characters) <br/>身份证/护照号码 (最后4个号码、字母)
                                        b-td XXXX
                                    b-tr
                                        b-td Contact No. <br/>联络电话
                                        b-td XXXX XXXX
                                    b-tr
                                        b-td Email address <br/>电邮地址
                                        b-td XXX@example.com
                            p.mb-0 *All personal information collected is kept strictly confidential.
                            p *所有收集到的个人资料将严格保密。
                    b-tab.p-3(title="Preview (Digital Exam)")
                        p.font-weight-bold.mb-0 Subject:
                        div {{ otherDetails.titleEn }} - {{otherDetails.titleZh}}
                        br
                        .border.py-3.px-4
                            .d-flex.justify-content-between
                                b-img(src="/tenglogo.png" style="height:120px")
                                b-img(src="/confinst.png" style="height:120px")
                            hr
                            p.mb-0 
                                strong Candidate Number 考生编号:
                                span.ml-4 XXXXXX
                            p 
                                strong Candidate Name 考生名字:
                                span.ml-4 XXXXXX
                            br
                            h5(class="mb-0") <u>{{ otherDetails.titleEn }}</u>
                            h5 <u>{{ otherDetails.titleZh }}</u>
                            br
                                                
                            p.mb-0 Thank you for your registration. Your exam date and time are scheduled as follow:
                            p 感谢您报名参加华乐器乐考级。您的考级日期及时间如下：
                            .px-5
                                b-table-simple(bordered)
                                    b-tbody
                                        b-tr
                                            b-th Video Submission Deadline <br/>视频上传截止日期
                                            b-th {{ startEn }}(23:59 hr) <br/>{{ startZh }}（23:59时）
                                        b-tr
                                            b-th URL Link to Upload Video  <br/> 视频上传网址
                                            b-th
                                                a(:href="otherDetails.digitalSubmitLink") {{otherDetails.digitalSubmitLink}}  


                            p.mb-0 
                                a(:href="otherDetails.examNotesLink") Please click here to view and download the Guidelines on Video Recording and Submission document. 
                            p 
                                a(:href="otherDetails.examNotesLink") 请点击这里查阅及下载视频录制和上传准则文件。

                            p.mb-0 Kindly also verify your particulars below. If there are any amendments to be made, please email us before <u>{{dayjs(otherDetails.amendmentsDeadline).format("DD MMMM YYYY (ddd)")}}</u>. No action is required if all particulars are correct.
                            p 请您也检查并确认以下个人资料。若有任何资料需要修改，请在<u>{{formatChineseDate(otherDetails.amendmentsDeadline)}}</u>之前电邮通知我们。若所有资料无误，您无需采取任何行动。

                            p.mb-0.font-weight-bold The certificate cannot be reprinted once verification is done.
                            p.font-weight-bold 一旦确认完成，证书将不得重印。
                            
                            p.mb-0 If you have any queries, kindly contact us at:
                            p 若有任何疑问，可拨电或电邮：

                            p.font-weight-bold {{otherDetails.contactDetails}}

                            p.mb-0 Thank you for your support. All the best for your examination!
                            p 感谢您的支持。祝您考级顺利！

                            hr
                            b-table-simple(bordered)
                                b-tbody
                                    b-tr 
                                        b-td Candidate Number<br/>考生编号
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Name of Candidate <br/>考生姓名
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Name in Chinese  <br/>中文名
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Instrument   <br/>乐器
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Grade    <br/>级别
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Exam Score Serial No.     <br/>乐谱编号
                                        b-td XXXXXX
                                    b-tr 
                                        b-td Instructor    <br/>指导老师
                                        b-td XXXXXX
                                    b-tr
                                        b-td Results Mailing Option    <br/>考级成绩邮寄方式
                                        b-td XXXXXX
                                    b-tr
                                        b-td Mailing address <br/>地址
                                        b-td XXXX XXXXXX, Singapore XXXXX
                            p.mb-0
                                i The following information is for purposes related to the Examinations and identification of candidate.
                            p
                                i 以下资料只供于考级相关用途及确认考生身份。
                            b-table-simple(bordered)
                                b-tbody
                                    b-tr
                                        b-td NRIC/Passport No (Last 4 Characters) <br/>身份证/护照号码 (最后4个号码、字母)
                                        b-td XXXX
                                    b-tr
                                        b-td Contact No. <br/>联络电话
                                        b-td XXXX XXXX
                                    b-tr
                                        b-td Email address <br/>电邮地址
                                        b-td XXX@example.com
                            p.mb-0 *All personal information collected is kept strictly confidential.
                            p *所有收集到的个人资料将严格保密。
                            hr
                            div {{otherDetails.digitalAddInstPreEn}}
                            div {{otherDetails.digitalAddInstPreZh}}
                            div
                            b-table-simple(bordered)
                                b-tbody
                                    b-tr 
                                        b-td
                                            ol
                                                li(v-for="(v,i) in digitalAddInstEnArr") {{digitalAddInstEnArr[i]}}<br>{{digitalAddInstZhArr[i]}}

                                        




</template>
<script>
import { PublicClientApplication } from "@azure/msal-browser";
//import pLimit from 'p-limit';
const msalConfig = {
    auth: {
        clientId: '26e47606-7deb-4a2c-843b-e2a73305da33', //use the teng account we use.
        authority: 'https://login.microsoftonline.com/691658ab-fa25-4817-9062-deabdf137aae'//their tenant id
    }
};
import XLSX from "xlsx"
import dayjs from "dayjs";
import axios from "axios";
import emailTemplate from "./assets/emailTemplate.html";
import emailTemplateDigital from "./assets/emailTemplateDigital.html";
import pdfTemplate from "./assets/pdfTemplate.html";
import pdfTemplateDigital from "./assets/pdfTemplateDigital.html";
import emailCSS from "./assets/emailCSS.min.html";
import pdfCSS from "./assets/pdfCSS.min.html";
import html2pdf from "html2pdf.js";

const msalInstance = new PublicClientApplication(msalConfig);
const daylistZh ="日一二三四五六"

export default {
  name: "Notif",
  props:["start"],
  data(){
    return {
        digitalMode:false,
        sendingEmail:false,
        pendingSend:null,
        showRowPreview:false,
        rowPreview:null,
        token:null,
        account:null,
        resumeSending:null,
        sheets:[],
        showEmailPreview:false,
        sheetNames:[],
        otherDetails: this.genOtherDetails(),
        fields:[
            "Candidate Number",
            "Full Name",  
            "Partial IC",
            "Address",
            "Postal Codes",
            "Date",
            "Reporting Time",
            "Chinese Name",
            "Instrument",
            "乐器",
            "Grade",
            "级别",
            "Book Serial Number",
            "Instructor",
            "指导老师",
            "Bulk Posting",
            "Local Registered Mail",
            "Overseas Registered Mail",
            "Phone Number",
            "Email",
            
        ]
    }
  },    
  methods:{
    dayjs,
    saveOtherDetails(key, value){
        localStorage.setItem("notif_settings_"+key,value);
    },
    genOtherDetails(){
        let output ={
            titleEn:"NOTIFICATION: CHINESE INSTRUMENTAL MUSIC GRADING EXAMINATIONS 2026",
            titleZh:"2026年华乐器乐考级通知书",
            venue:`Singapore Chinese Cultural Centre 新加坡华族文化中心
Creative Box (Level 6) 创意室（6楼）
1 Straits Boulevard, Singapore 018906

Nearest MRT: Tanjong Pagar / Downtown / Shenton Way
邻近地铁站：丹戎巴葛 / 市中心 / 珊顿道`,
            examNotesLink:"http://teng-schedule.certainty.is/Schedule-2024-(DSA)-Important-Notes.pdf",
            examAcknowledgementLink: "https://registration.grading.thetengcompany.com/exam-details-acknowledgement/",
            amendmentsDeadline:"2026-10-31",
            contactDetails:"Mobile: 9648 8771, Email: grading@thetengcompany.com",
            digitalSubmitLink:"https://bit.ly/digitalCIMGE2026",
            digitalAddInstPreEn:"For candidates taking the Examination for Certificate In Professional Performance:",
            digitalAddInstPreZh:"演奏文凭级别考生请注意:",
            digitalAddInstEn:"Candidates must be in their performance position where they are required to recite their name, candidate number and the titles of their three pieces before performing. Candidates are required to start performing with their set piece in the video recording.\nIf you face difficulties with the form and/or video submission, please write in to<br/>onlinegrading@thetengcompany.com",
            digitalAddInstZh:"考生必须在演奏位置上，在开始演奏前报上考生名字、考生编号和三首演奏曲名。考生必须从指定曲开始演奏。\n如果您在提交视频或表格遇到问题或有任何疑问，请发电子邮电至 onlinegrading@thetengcompany.com 与我们联系。"
        };

        for(let key in output){
            let saved = localStorage.getItem("notif_settings_"+key);

            if(saved!==null){
                output[key] = saved;
            }

        }

        return output
    },
    getGraphApi(){
        return axios.create({
            baseURL:`https://graph.microsoft.com/v1.0/`,
            headers:{
                "Accept": "application/json",
                "Authorization":`Bearer ${this.token}`,
                "Content-Type": "application/json; charset=utf-8",
            }
        })
    },
    login(){
      return msalInstance.loginPopup({
          redirectUri: `${location.origin}/blank.html`,
          scopes: ["Mail.Send.Shared"],
          prompt: "select_account"
      }).then(rsp=>{
            this.account = rsp.account;
            this.token =rsp.accessToken;
            return true;
      }).catch(()=>{
        alert("Cannot Login. Please try again");
        return false;
      })
    },
    // A fresh token for each email: MSAL renews it in the background, so a long run doesn't expire half-way
    async getToken(){
        const rsp = await msalInstance.acquireTokenSilent({
            redirectUri: `${location.origin}/blank.html`,
            scopes: ["Mail.Send.Shared"],
            account: this.account
        });
        this.token = rsp.accessToken;
    },
    async processXLSX(file){
        let arrB = await file.arrayBuffer();
        let wb = XLSX.read(arrB,{raw:false,cellDates:true});
        this.sheets = []
        this.sheetNames = [];
        for (let wsName of wb.SheetNames){
            if(wsName == "Unassigned Students"){
                continue;
            }
            let ws = wb.Sheets[wsName];
            let content = XLSX.utils.sheet_to_json(ws,{raw:false,dateNF:'yyyy-mm-dd'});
            for(let row of content){
                for(let key in row){
                    if(row[key]=="null"){
                        row[key] ="";
                    }
                }
            }
            if(content.length > 0){
                this.sheets.push(content);
                this.sheetNames.push(wsName);
            }
            

        }

    },
    convertFragment(fragment){
        let div=document.createElement("div");
        div.appendChild(fragment);
        return div.innerHTML;

    },
    checkGrade(value) {
      if (!value) return '-';
    
      const gradeOptions = [
        { value: "Grade 1", text: "一级"},
        { value: "Grade 2", text: "二级"},
        { value: "Grade 3", text: "三级"},
        { value: "Grade 4", text: "四级"},
        { value: "Grade 5", text: "五级"},
        { value: "Grade 6", text: "六级"},
        { value: "Grade 7", text: "七级"},
        { value: "Grade 8", text: "八级"},
        { value: "Certificate In Professional Performance", text: "演奏文凭"},
      ];
    
    
      const found = gradeOptions.find(opt => opt.value === String(value));
      return found ? found.text : '-';
    },
    checkInstrumentType(value) {
      if (!value) return '-';
    
      const instrumentOptions = [
        { value: "Guzheng", text: "古筝"},
        { value: "Erhu", text: "二胡"},
        { value: "Dizi", text: "笛子"},
        { value: "Pipa", text: "琵琶"},
        { value: "Yangqin", text: "扬琴"},
        { value: "Ruan", text: "阮"},
        { value: "Liuqin", text: "柳琴"},
        { value: "Gaoyin Sheng", text: "高音笙"},
        { value: "Zhongyin Sheng", text: "中音笙"},
        { value: "Gaoyin Suona", text: "高音唢呐"},
        { value: "Zhongyin Suona", text: "中音唢呐"},
        { value: "Cello", text: "大提琴"},
        { value: "Double Bass", text: "倍大提琴"},
        { value: "Hulusi", text: "葫芦丝"},
        { value: "Guanzi", text: "管子"},
        { value: "Percussion", text: "打击乐"},
        { value: "Guqin", text: "古琴"},
        { value: "Bawu", text: "巴乌"}
      ];
    
      const found = instrumentOptions.find(opt => opt.value === String(value));
      return found ? found.text : '-';
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    hasChinese(text) {
      return /[\u4e00-\u9fa5]/.test(text);
    },
    hasEnglish(text) {
      return /[A-Za-z]/.test(text);
    },
    // Builds the email exactly as it will be sent; used by both Preview and Send
    buildEmail(line) {
        let { titleEn, titleZh, venue, examNotesLink, examAcknowledgementLink, amendmentsDeadline, contactDetails, digitalSubmitLink, digitalAddInstPreEn, digitalAddInstPreZh } = this.otherDetails;
        let { digitalAddInstEnArr, digitalAddInstZhArr } = this;
        let title = titleEn + " - " + titleZh;
        let canNo = line["Candidate Number"];
        let fullName = line["Full Name"];
        let dateOfExam = line["Date"];
        let reportingTime = line["Reporting Time"];
        let nameZH = line["Chinese Name"];
        let inst = line["Instrument"];
        let instZh = this.checkInstrumentType(line["Instrument"]);//line["乐器"];
        let grade = line["Grade"];
        let gradeZh = this.checkGrade(line["Grade"]); //line["级别"];
        let bookSN = (line["Book Serial Number"] !=='' && line["Book Serial Number"] !==null && line["Book Serial Number"] !==undefined) ? line["Book Serial Number"] : '-';
        let instructor = (line["Instructor"] !=='' && line["Instructor"] !==null && line["Instructor"] !==undefined && this.hasEnglish(line["Instructor"])) ? line["Instructor"] : '-';
        let instructorZh = (line["Instructor"] !=='' && line["Instructor"] !==null && line["Instructor"] !==undefined && this.hasChinese(line["Instructor"])) ? line["Instructor"] : ((line["指导老师"] !=='' && line["指导老师"] !==null && line["指导老师"] !==undefined) ? line["指导老师"] : '-');
        let instructorEmail = line["Instructor Email"];
        let phone = line["Phone Number"];
        let email = line["Email"];
        let partialIC = line["Partial IC"];
        let mailOption = line["Bulk Posting"] == "Yes" ? "⁠Bulk Posting to Schools or Instructors" : line["Overseas Registered Mail"] == "Yes" ? "Overseas Registered Mail" : "Local Registered Mail";
        let mailOptionZh = line["Bulk Posting"] == "Yes" ? "集体邮寄至学校或指导老师" : line["Overseas Registered Mail"] == "Yes" ? "海外挂号邮件" : " 本地附加追踪邮件";
        let dateOfExamEn = dayjs(dateOfExam).format("D MMM YYYY (dddd)");
        let dateOfExamZh = this.formatChineseDate(dateOfExam);
        let amendmentsDeadlineEn = dayjs(amendmentsDeadline).format("D MMM YYYY (dddd)");
        let amendmentsDeadlineZh = this.formatChineseDate(amendmentsDeadline);
    
        const urlVenue = venue.split("\n").map(line => line.trim());
        const formattedUrlVenue = urlVenue.map((line, index) => {
            if (index === 2) {
                return `${line}\n`;
            }
            if (index === urlVenue.length - 1) {
                return `${line}`;
            }
            return `${line}\n`;
        }).join("");

        const digitalSubmitLinkParam = digitalSubmitLink ? `${encodeURIComponent(digitalSubmitLink)}` : '';
        //console.log(digitalSubmitLinkParam)

        const queryParams = new URLSearchParams({
            examDate: dayjs(dateOfExam).format("D MMM YYYY"),
            reportingTime: this.digitalMode ? "" : (reportingTime || ""),
            venue: this.digitalMode ? "" : formattedUrlVenue,
            candidateNumber: canNo || "",
            digitalSubmitLink: this.digitalMode ? digitalSubmitLinkParam : "",
            examNotesLink: examNotesLink,
            instructorChineseName: instructorZh === '-' ? '' : instructorZh,
            instructorEnglishName: instructor === '-' ? '' : instructor,
            bookSN: bookSN === '-' ? '' : bookSN,
        });

        //console.log(this.digitalMode, digitalSubmitLinkParam);
        //console.log(this.digitalMode===true ? digitalSubmitLinkParam : "")
    
        let venueLines = venue.split("\n");
        venue = venueLines.map(line => `<div>${line.trim()} &nbsp;</div>`).join("");
    
        examAcknowledgementLink = examAcknowledgementLink + line["(Do Not Modify) Registration Record"] + "?" + queryParams.toString();
    
        let address = `${line["Address"]} ${!line["Postal Codes"] ? "" : ", Singapore " + line["Postal Codes"]}`;
        let mergeIn = {
            titleEn,
            titleZh,
            venue,
            examNotesLink,
            examAcknowledgementLink,
            amendmentsDeadlineEn,
            amendmentsDeadlineZh,
            contactDetails,
            canNo,
            fullName,
            nameZH,
            dateOfExamEn,
            dateOfExamZh,
            reportingTime,
            inst,
            instZh,
            grade,
            gradeZh,
            bookSN,
            instructor,
            instructorZh,
            phone,
            email,
            mailOption,
            mailOptionZh,
            partialIC,
            address, digitalSubmitLink,
            digitalAddInstPreEn, digitalAddInstPreZh,
            digitalAddInstEnArr, digitalAddInstZhArr
        };
    
        let html = document.createRange().createContextualFragment(`<html><head>${emailCSS}</head><body>${this.digitalMode ? emailTemplateDigital : emailTemplate}</body></html>`);
        let pdfHtml = document.createRange().createContextualFragment(`<html><head>${pdfCSS}</head><body>${this.digitalMode ? pdfTemplateDigital : pdfTemplate}</body></html>`);
    
        [...html.querySelectorAll("[data-template]")].forEach(el => {
            let sel = el.getAttribute("data-template");
            el.innerHTML = mergeIn[sel] || "";
        });
    
        [...html.querySelectorAll("[data-href]")].forEach(el => {
            let sel = el.getAttribute("data-href");
            el.setAttribute("href", mergeIn[sel]);
        });
    
        [...html.querySelectorAll("[data-list]")].forEach(el => {
            let sel = el.getAttribute("data-list");
            let output = "";
            let en = mergeIn[sel + 'EnArr'] || [];
            let zh = mergeIn[sel + 'ZhArr'] || [];
            for (let i = 0; i < mergeIn[sel + 'EnArr'].length; i++) {
                output += `<li>${en[i]}<br/>${zh[i]}</li>`;
            }
            el.innerHTML = output;
        });
    
        [...pdfHtml.querySelectorAll("[data-template]")].forEach(el => {
            let sel = el.getAttribute("data-template");
            el.innerHTML = mergeIn[sel] || "";
        });
    
        [...pdfHtml.querySelectorAll("[data-href]")].forEach(el => {
            let sel = el.getAttribute("data-href");
            el.setAttribute("href", mergeIn[sel]);
        });
    
        [...pdfHtml.querySelectorAll("[data-list]")].forEach(el => {
            let sel = el.getAttribute("data-list");
            let output = "";
            let en = mergeIn[sel + 'EnArr'] || [];
            let zh = mergeIn[sel + 'ZhArr'] || [];
            for (let i = 0; i < mergeIn[sel + 'EnArr'].length; i++) {
                output += `<li>${en[i]}<br/>${zh[i]}</li>`;
            }
            el.innerHTML = output;
        });
    
        // Each [data-pdf-page] block of the PDF template starts on a new page (see generatePdf)
        let pdfPages = [...pdfHtml.querySelectorAll("[data-pdf-page]")].map(el => pdfCSS + el.outerHTML);
        let body = this.convertFragment(html);
        let pdfBody = this.convertFragment(pdfHtml);
    
        let emailCC = [
            {
                emailAddress: {
                    name: "Grading",
                    address:"grading@thetengcompany.com"  // don't cc them 
                },
            }
        ];
        let emailBCC = [
            {
                emailAddress: {
                    name: "Academy Instructor",
                    //address: "academy@thetengcompany.com"
                    address: "onlinegrading@thetengcompany.com"
                },
            }
        ];
    
        if (instructorEmail) {
            emailCC.push({
                emailAddress: {
                    name: instructor || "Academy Instructor",
                    address: instructorEmail.replace(/\u00A0/g, "").replace(/\s+/g, "").trim()
                },
            });
        } 
        /*else {
            emailBCC.push({
                emailAddress: {
                    name: "Academy Instructor",
                    //address: "academy@thetengcompany.com"
                    address: "onlinegrading@thetengcompany.com"
                },
            });
        }*/

        return {
            title,
            toRecipients: [
                {
                    emailAddress: {
                        name: fullName,
                        address: (email || "").replace(/\u00A0/g, "").replace(/\s+/g, "").trim()
                    }
                }
            ],
            ccRecipients: emailCC,
            bccRecipients: emailBCC,
            body,
            pdfBody,
            pdfPages,
            fileName: `${dayjs().format('YYYY-MM-DD')}_${fullName.replace(/ /g, '_')}_Exam_Confirmation.pdf`,
            digitalMode: this.digitalMode,
            problems: this.rowProblems(line)
        };
    },
    // Reasons a row can't be sent; an empty Date would otherwise show today's date as the deadline
    rowProblems(row) {
        let problems = [];
        if (!String(row["Email"] || "").trim()) {
            problems.push("no address in the Email column");
        }
        if (!row["Date"] || !dayjs(row["Date"]).isValid()) {
            problems.push("no readable Date (write it like 2026-11-16 or 16 Nov 2026)");
        }
        // Digital exams have a fixed 23:59 deadline, so only physical exams need a Reporting Time
        if (!this.digitalMode && !String(row["Reporting Time"] || "").trim()) {
            problems.push("no Reporting Time (needed for physical exams)");
        }
        return problems;
    },
    generatePdf(msg) {
        const pdfOptions = {
            margin: !msg.digitalMode ? [7, 10, 6.8, 10] : [16, 10, 15.5, 10],
            filename: msg.fileName,
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        };
        // Render each page block on its own so it always starts on a new page; templates without blocks render whole
        const [first, ...rest] = msg.pdfPages.length ? msg.pdfPages : [msg.pdfBody];
        let worker = html2pdf().set(pdfOptions).from(first).toPdf();
        for (const page of rest) {
            worker = worker.get('pdf').then(pdf => { pdf.addPage(); }).from(page).toContainer().toCanvas().toPdf();
        }
        return worker.outputPdf('blob');
    },
    async sendOneEmail(line) {
        const msg = this.buildEmail(line);
        if (msg.problems.length) {
            throw new Error(msg.problems.join(" and "));
        }
        const pdf = await this.generatePdf(msg);
        const base64Pdf = await this.blobToBase64(pdf);

        try {
            await this.getToken();
        } catch (error) {
            throw Object.assign(error, { needsSignIn: true });
        }
        const $ = this.getGraphApi();
        await $.post("/me/sendMail", {
            message: {
                from: {
                    emailAddress: {
                        name: "Registration",
                        //address: "tq-admin-2@thetengcompany.onmicrosoft.com"
                        address:"registration@noreply.thetengcompany.com" // temp overrive with our own email
                    }
                },
                replyTo: [
                    {
                        emailAddress:{
                            name: "Grading",
                            address:"grading@thetengcompany.com" // don't email to them 
                        }
                    }
                ],
                toRecipients: msg.toRecipients,
                ccRecipients: msg.ccRecipients,
                bccRecipients: msg.bccRecipients,
                subject: msg.title,
                body: {
                    contentType: "html",
                    content: msg.body
                },
                attachments: [
                    {
                        "@odata.type": "#microsoft.graph.fileAttachment",
                        name: msg.fileName,
                        contentType: "application/pdf",
                        contentBytes: base64Pdf
                    }
                ]
            },
            saveToSentItems: "true"
        });
    },
    // If the sign-in has run out, wait for the user to sign in again and retry the same row instead of skipping it
    async sendWithReauth(line) {
        for (;;) {
            try {
                return await this.sendOneEmail(line);
            } catch (error) {
                let signInExpired = error.needsSignIn || (error.response && error.response.status === 401);
                if (!signInExpired) throw error;
                if (!await new Promise(resolve => { this.resumeSending = resolve; })) {
                    throw Object.assign(new Error("sending was stopped"), { stopped: true });
                }
            }
        }
    },
    async signInAndResume() {
        if (await this.login()) {
            this.resumeSending(true);
            this.resumeSending = null;
        }
    },
    stopSending() {
        this.resumeSending(false);
        this.resumeSending = null;
    },
    // Show the configuration first so links/dates can be checked before anything is sent
    confirmSend(arr) {
        this.pendingSend = arr.flat();
        this.showEmailPreview = true;
    },
    async sendPending() {
        let rows = this.pendingSend;
        // Date/time problems: make them confirm they really want to send
        if (this.dateWarnings.length) {
            const h = this.$createElement;
            let confirmed = await this.$bvModal.msgBoxConfirm([
                h("p", "Please check the following:"),
                h("ul", this.dateWarnings.map(warning => h("li", warning))),
                h("p", { class: "mb-0 font-weight-bold" }, `Do you still want to send ${this.pendingSendable} email(s)?`)
            ], {
                title: "Check before sending",
                size: "lg",
                centered: true,
                okTitle: "Yes, send anyway",
                okVariant: "danger",
                cancelTitle: "No, go back",
            });
            if (!confirmed) return;
        }
        this.showEmailPreview = false;
        this.sendEmail(rows);
    },
    async previewRow(line) {
        this.clearRowPreview();
        let preview;
        try {
            preview = { ...this.buildEmail(line), pdfUrl: null };
        } catch (error) {
            console.error("Error building preview:", error);
            alert(`Could not build the email for ${line["Full Name"] || "this row"}: ${error.message}`);
            return;
        }
        this.rowPreview = preview;
        this.showRowPreview = true;
        const pdf = await this.generatePdf(preview);
        // Skip if the preview was closed or another row was opened meanwhile
        if (this.rowPreview === preview) {
            preview.pdfUrl = URL.createObjectURL(pdf);
        }
    },
    clearRowPreview() {
        if (this.rowPreview && this.rowPreview.pdfUrl) {
            URL.revokeObjectURL(this.rowPreview.pdfUrl);
        }
        this.rowPreview = null;
    },
    formatRecipients(list) {
        return list.map(r => r.emailAddress.name ? `${r.emailAddress.name} <${r.emailAddress.address}>` : r.emailAddress.address).join(", ");
    },
    async sendEmail(arr) {
        this.sendingEmail = true;
        let flatArr = arr.flat();
        let count = 0;
        let errArr = [];
        //const limit = pLimit(1);

        const batchSize = 1; // send 20 emails per batch
        const delay = ms => new Promise(r => setTimeout(r, ms));

        let stopped = false;

        for (let i = 0; i < flatArr.length; i += batchSize) {
            const batch = flatArr.slice(i, i + batchSize);

            await Promise.all(batch.map(async (line, index) => {
              try {
                if (stopped) throw new Error("sending was stopped");
                await this.sendWithReauth(line);
                count++;
              } catch (error) {
                console.error("Error sending email:", error);
                stopped = stopped || !!error.stopped;
                errArr.push({ row: i + index + 1, fullName: line["Full Name"], email: line["Email"], reason: error.message });
              }
            }));

            // wait 3 seconds between batches to avoid rate limits
            if (!stopped) await delay(3000);
        }

        /*await Promise.all(flatArr.map((line,index) =>
            limit(() =>
                this.sendOneEmail(
                    line
                ).then(() => count++)
                .catch(error => {
                    console.error("Error sending email:", error);
                    errArr.push({ row:index+1, fullName: line["Full Name"], email: line["Email"] });
                })
            )
        ));*/
    
        alert(`Sent ${count} emails successfully`);
        console.log(`Sent ${count} emails successfully`)
        if (errArr.length > 0) {
            alert(`Could not send notifications to the following emails:\n\n${errArr.map(r => r.fullName + ':<' + r.email + '> - ' + r.reason).join("\n")}`);
            console.log(`Could not send notifications to the following emails:\n\n${errArr.map(r => 'Row ' +r.row + ' ' +r.fullName + ':<' + r.email + '> - ' + r.reason).join("\n")}`);
        }
        this.sendingEmail = false;
    },
    formatChineseDate(str){
        let day = daylistZh[dayjs(str).day()];
        return dayjs(str).format(`YYYY 年 M 月 D 日 (星期${day})`)

    },
    blobToBase64(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
  },
  computed:{
    // Uploaded workbook's date (the one emails actually use), falling back to Base Configuration start
    previewDate(){
        let firstRow = (this.pendingSend || this.sheets.flat())[0];
        return firstRow && firstRow["Date"] ? firstRow["Date"] : this.start;
    },
    pendingUnsendable(){
        return (this.pendingSend || []).filter(row => this.rowProblems(row).length).length;
    },
    pendingSendable(){
        return (this.pendingSend || []).length - this.pendingUnsendable;
    },
    // Compares the years in the uploaded entries' Date with the years written in the configuration
    dateWarnings(){
        let rows = this.pendingSend || this.sheets.flat();
        if (!rows.length) return [];
        let warnings = [];
        let years = {};
        let unreadable = 0;
        for (let row of rows) {
            let date = dayjs(row["Date"]);
            if (!row["Date"] || !date.isValid()) {
                unreadable++;
                continue;
            }
            years[date.year()] = (years[date.year()] || 0) + 1;
        }
        let examYears = Object.keys(years);
        let entriesHave = n => `${n} entr${n > 1 ? "ies have" : "y has"}`;
        if (unreadable) {
            warnings.push(`${entriesHave(unreadable)} an empty or unreadable Date and will not be sent (write it like 2026-11-16 or 16 Nov 2026).`);
        }
        // Only physical exams need a Reporting Time; digital exams have a fixed 23:59 deadline.
        // Start Time / End Time aren't used in the email, so they aren't checked.
        if (!this.digitalMode) {
            let noReportingTime = rows.filter(row => !String(row["Reporting Time"] || "").trim()).length;
            if (noReportingTime) {
                warnings.push(`${entriesHave(noReportingTime)} no Reporting Time and will not be sent.`);
            }
        }
        if (examYears.length > 1) {
            warnings.push(`The entries' Dates are in different years: ${examYears.map(y => `${y} (${years[y]} entr${years[y] > 1 ? "ies" : "y"})`).join(", ")}.`);
        }
        if (!examYears.length) return warnings;
        let fields = [
            ["Email Subject (English)", this.otherDetails.titleEn],
            ["Email Subject (Chinese)", this.otherDetails.titleZh],
            ["Deadline for details amendments", this.otherDetails.amendmentsDeadline],
            ["Link to notes / Guidelines", this.otherDetails.examNotesLink],
        ];
        if (this.digitalMode) {
            fields.push(["Link to submission for Digital Exam", this.otherDetails.digitalSubmitLink]);
        }
        for (let [label, value] of fields) {
            let fieldYears = (String(value || "").match(/\d+/g) || []).filter(n => /^20\d\d$/.test(n));
            let wrong = [...new Set(fieldYears)].filter(y => !years[y]);
            if (wrong.length) {
                warnings.push(`${label} says ${wrong.join(", ")}, but the entries' exam Date is in ${examYears.join(", ")}.`);
            }
        }
        return warnings;
    },
    startEn(){
        return dayjs(this.previewDate).format("D MMM YYYY (dddd)")
    },
    startZh(){
        return this.formatChineseDate(this.previewDate);
    },
    tableFields(){
        return [...this.fields,"Actions"]
    },
    digitalAddInstEnArr(){
        let arr =[];
        try {
            arr = this.otherDetails.digitalAddInstEn.split("\n");
 
        } catch (error) {
            arr =[];
        }

        while(this.digitalAddInstZhArr.length > arr.length){
            arr.push("");
        }
        return arr
    },
    digitalAddInstZhArr(){
        let arr =[];
        try {
            arr = this.otherDetails.digitalAddInstZh.split("\n");
 
        } catch (error) {
            arr =[];
        }    
        return arr

    }
  },
}

</script>
